import {
  type CustomerApplicationFieldValueContract,
  type CustomerApplicationSourcePreviewContract,
  type CustomerFieldMappingTarget,
  type ListQuestionnaireSourceRecordsParams,
  ListQuestionnaireSourceRecordsParamsSchema,
  type ListQuestionnaireSourceRecordsResult,
  type QuestionnaireSourceRecordArchiveStatus,
  type QuestionnaireSourceRecordContract,
} from '@/contracts/customer-application-binding'
import { type CustomerApplicationContract, createDuplicateSignals } from '@/contracts/customers'
import { createSyntheticMeta } from '@/contracts/questionnaire'
import { type CustomerApplication, type SalesUser, normalizeEmail } from '@/lib/business-workflow'
import {
  CustomerApplicationBindingError,
  buildApplicationFromSourceRecord,
  buildFieldValuesFromSourceRecord,
  buildManualApplication,
  buildManualFieldValues,
  buildSourceSummary,
  ensureSourceRecordAvailable,
  listQuestionnaireSourceRecords,
  overrideApplicationFieldValue,
} from '@/lib/customer-application-binding'
import {
  type BusinessWorkflowSnapshot,
  loadFixture,
  saveBusinessWorkflowSnapshot,
} from '@/lib/mock-api/persistent-store'
import type { SessionUser } from '@/types/api'

type CurrentUser = Pick<SessionUser, 'email' | 'name'> | null | undefined

export type CustomerApplicationSourceCreateInput = {
  sourceRecordId: string
  currentUser?: CurrentUser
  overrides?: Partial<Record<CustomerFieldMappingTarget, string | number | undefined>>
}

export type CustomerApplicationManualInput = Pick<
  CustomerApplication,
  | 'formId'
  | 'formTitle'
  | 'formDescription'
  | 'companyName'
  | 'taxId'
  | 'industry'
  | 'companySize'
  | 'website'
  | 'companyAddress'
  | 'companyPhone'
  | 'contactName'
  | 'contactTitle'
  | 'contactEmail'
  | 'contactPhone'
  | 'decisionRole'
  | 'paymentTerms'
  | 'creditLimit'
  | 'salesRegion'
  | 'expectedValue'
  | 'source'
  | 'needSummary'
  | 'internalNotes'
>

export type CustomerApplicationManualCreateInput = {
  application: CustomerApplicationManualInput
  currentUser?: CurrentUser
}

function createId(prefix: string) {
  return `${prefix}-${crypto.randomUUID()}`
}

function toSubmittedAt() {
  return new Date().toLocaleString('zh-TW', { hour12: false })
}

function paginate<T>(items: T[], page: number, limit: number) {
  const start = (page - 1) * limit
  return items.slice(start, start + limit)
}

function resolveSubmitter(currentUser: CurrentUser, salesUsers: SalesUser[]) {
  const matched = salesUsers.find(
    (salesUser) => normalizeEmail(salesUser.email) === normalizeEmail(currentUser?.email),
  )
  if (matched) return matched
  return salesUsers.find((salesUser) => salesUser.role !== 'manager') ?? salesUsers[0]
}

function resolveSupervisor(submitter: SalesUser, salesUsers: SalesUser[]) {
  return (
    salesUsers.find(
      (salesUser) => normalizeEmail(salesUser.email) === normalizeEmail(submitter.supervisorEmail),
    ) ?? submitter
  )
}

function buildRecords(snapshot = loadFixture()): QuestionnaireSourceRecordContract[] {
  return listQuestionnaireSourceRecords({
    forms: snapshot.forms,
    responses: snapshot.responses,
    sourceLinks: snapshot.businessWorkflow.sourceLinks,
    applications: snapshot.businessWorkflow.applications,
    salesUsers: snapshot.businessWorkflow.salesUsers,
    formDetails: snapshot.formDetails,
    fieldBindings: snapshot.businessWorkflow.fieldBindings,
  })
}

function matchesAnyKeyword(
  values: Array<string | number | undefined>,
  keyword: string | undefined,
) {
  if (!keyword) return true
  const normalized = keyword.trim().toLocaleLowerCase()
  if (!normalized) return true
  return values.some((value) =>
    String(value ?? '')
      .toLocaleLowerCase()
      .includes(normalized),
  )
}

function toApplicationContract(
  application: CustomerApplication,
  workflow: BusinessWorkflowSnapshot,
  records: QuestionnaireSourceRecordContract[],
): CustomerApplicationContract {
  return {
    ...application,
    duplicateSignals: createDuplicateSignals(application, workflow.customers),
    sourceSummary: buildSourceSummary(application, workflow.sourceLinks, records),
    fieldValues: workflow.fieldValues[application.id] ?? [],
    sourceMeta: createSyntheticMeta(),
  }
}

function saveWorkflow(nextWorkflow: BusinessWorkflowSnapshot) {
  return saveBusinessWorkflowSnapshot(nextWorkflow)
}

export async function listMockQuestionnaireSourceRecords(
  params: ListQuestionnaireSourceRecordsParams = {},
): Promise<ListQuestionnaireSourceRecordsResult> {
  const parsed = ListQuestionnaireSourceRecordsParamsSchema.parse(params)
  const records = buildRecords()
  const filtered = records.filter((record) => {
    const matchesForm = !parsed.formId || record.formId === parsed.formId
    const matchesSalesUser = !parsed.salesUserId || record.salesUserId === parsed.salesUserId
    const matchesLinkStatus = parsed.linkStatus === 'all' || record.linkStatus === parsed.linkStatus
    const matchesArchiveStatus =
      parsed.archiveStatus === 'all' || (record.archiveStatus ?? 'active') === parsed.archiveStatus
    const matchesKeyword = matchesAnyKeyword(
      [
        record.formTitle,
        record.submittedByName,
        record.submittedByEmail,
        record.salesUserName,
        record.linkedApplicationNumber,
        record.linkedApplicationStatus,
        ...record.firstAnswers.map((answer) => answer.valuePreview),
      ],
      parsed.keyword,
    )
    return (
      matchesForm && matchesSalesUser && matchesLinkStatus && matchesArchiveStatus && matchesKeyword
    )
  })

  return {
    data: paginate(filtered, parsed.page, parsed.limit),
    meta: {
      ...createSyntheticMeta(),
      total: filtered.length,
      page: parsed.page,
      limit: parsed.limit,
      archive_status_filter: parsed.archiveStatus,
    },
  }
}

export async function getMockQuestionnaireSourceRecord(sourceRecordId: string) {
  const record = buildRecords().find((item) => item.id === sourceRecordId)
  if (!record) {
    throw new CustomerApplicationBindingError('SOURCE_RECORD_NOT_FOUND', '找不到問卷記錄。', 404)
  }
  return record
}

export async function archiveMockQuestionnaireSourceRecord(
  sourceRecordId: string,
  archiveStatus: QuestionnaireSourceRecordArchiveStatus,
) {
  const record = buildRecords().find((item) => item.id === sourceRecordId)
  if (!record) {
    throw new CustomerApplicationBindingError('SOURCE_RECORD_NOT_FOUND', '找不到問卷記錄。', 404)
  }
  return {
    ...record,
    archiveStatus,
    archivedAt: archiveStatus === 'archived' ? new Date().toISOString() : undefined,
    sourceMeta: createSyntheticMeta(),
  }
}

export async function previewMockCustomerApplicationFromSourceRecord(
  sourceRecordId: string,
): Promise<CustomerApplicationSourcePreviewContract> {
  const state = loadFixture()
  const records = buildRecords(state)
  const record = ensureSourceRecordAvailable(sourceRecordId, records)
  const response = state.responses.find((item) => item.id === record.responseId) ?? null
  const questions = state.formDetails[record.formId]?.questions ?? []
  const bindings = state.businessWorkflow.fieldBindings.filter(
    (binding) => binding.formId === record.formId,
  )

  return {
    record,
    fieldValues: buildFieldValuesFromSourceRecord({
      record,
      response,
      questions,
      bindings,
    }),
    sourceMeta: createSyntheticMeta(),
  }
}

export async function createMockCustomerApplicationFromSourceRecord({
  sourceRecordId,
  currentUser,
  overrides = {},
}: CustomerApplicationSourceCreateInput): Promise<CustomerApplicationContract> {
  const state = loadFixture()
  const records = buildRecords(state)
  const record = ensureSourceRecordAvailable(sourceRecordId, records)
  const response = state.responses.find((item) => item.id === record.responseId) ?? null
  const questions = state.formDetails[record.formId]?.questions ?? []
  const bindings = state.businessWorkflow.fieldBindings.filter(
    (binding) => binding.formId === record.formId,
  )

  let fieldValues = buildFieldValuesFromSourceRecord({
    record,
    response,
    questions,
    bindings,
  })
  for (const [fieldKey, value] of Object.entries(overrides) as Array<
    [CustomerFieldMappingTarget, string | number | undefined]
  >) {
    if (value === undefined) continue
    fieldValues = overrideApplicationFieldValue(fieldValues, fieldKey, String(value))
  }

  const submitter = resolveSubmitter(currentUser, state.businessWorkflow.salesUsers)
  const supervisor = resolveSupervisor(submitter, state.businessWorkflow.salesUsers)
  const application = buildApplicationFromSourceRecord({
    record,
    fieldValues,
    submitter,
    supervisor,
    applicationId: createId('app'),
    submittedAt: toSubmittedAt(),
  })
  const duplicateSignals = createDuplicateSignals(application, state.businessWorkflow.customers)
  const applicationWithStatus: CustomerApplication = {
    ...application,
    status: duplicateSignals.length > 0 ? 'duplicate_pending' : 'pending_review',
  }
  const nextWorkflow: BusinessWorkflowSnapshot = {
    ...state.businessWorkflow,
    applications: [applicationWithStatus, ...state.businessWorkflow.applications],
    sourceLinks: [
      {
        id: createId('source-link'),
        workspaceId: record.workspaceId,
        applicationId: applicationWithStatus.id,
        sourceRecordId: record.id,
        responseId: record.responseId,
        formId: record.formId,
        sourceType: 'questionnaire_record',
        status: 'active',
        createdAt: new Date().toISOString(),
        createdByEmail: submitter.email,
        sourceMeta: createSyntheticMeta(),
      },
      ...state.businessWorkflow.sourceLinks,
    ],
    fieldValues: {
      ...state.businessWorkflow.fieldValues,
      [applicationWithStatus.id]: fieldValues,
    },
  }

  const saved = saveWorkflow(nextWorkflow)
  return toApplicationContract(applicationWithStatus, saved, buildRecords(loadFixture()))
}

export async function createMockManualCustomerApplication({
  application: input,
  currentUser,
}: CustomerApplicationManualCreateInput): Promise<CustomerApplicationContract> {
  const state = loadFixture()
  const submitter = resolveSubmitter(currentUser, state.businessWorkflow.salesUsers)
  const supervisor = resolveSupervisor(submitter, state.businessWorkflow.salesUsers)
  const fieldValues = buildManualFieldValues(input)
  const application = buildManualApplication({
    input,
    submitter,
    supervisor,
    applicationId: createId('app'),
    submittedAt: toSubmittedAt(),
  })
  const duplicateSignals = createDuplicateSignals(application, state.businessWorkflow.customers)
  const applicationWithStatus: CustomerApplication = {
    ...application,
    status: duplicateSignals.length > 0 ? 'duplicate_pending' : 'pending_review',
  }
  const nextWorkflow: BusinessWorkflowSnapshot = {
    ...state.businessWorkflow,
    applications: [applicationWithStatus, ...state.businessWorkflow.applications],
    fieldValues: {
      ...state.businessWorkflow.fieldValues,
      [applicationWithStatus.id]: fieldValues,
    },
  }

  const saved = saveWorkflow(nextWorkflow)
  return toApplicationContract(applicationWithStatus, saved, buildRecords(loadFixture()))
}

export async function updateMockApplicationFieldValue(
  applicationId: string,
  fieldKey: CustomerFieldMappingTarget,
  nextValue: string,
): Promise<CustomerApplicationFieldValueContract[]> {
  const state = loadFixture()
  const values = state.businessWorkflow.fieldValues[applicationId] ?? []
  const nextValues = overrideApplicationFieldValue(values, fieldKey, nextValue)
  saveWorkflow({
    ...state.businessWorkflow,
    fieldValues: {
      ...state.businessWorkflow.fieldValues,
      [applicationId]: nextValues,
    },
  })
  return nextValues
}
